/** Shared activity-log labels and badge colours — matches Inventory / unit history. */

export type ActivitySource = 'movement' | 'request';

export interface ActivityLogRow {
  id: string;
  source: ActivitySource;
  createdAt: Date;
  actionKey: string;
  actionLabel: string;
  itemId: string | null;
  itemName: string | null;
  detail: string | null;
  user: string | null;
  reference: string | null;
  unitLabel: string | null;
  serialMac: string | null;
}

/** Same format as Inventory “Last Log”. */
export function fmtLogDateTime(d: Date | string | null | undefined): string {
  const date = d instanceof Date ? d : new Date(d ?? '');
  if (isNaN(date.getTime())) return '—';
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${day}/${month}/${year} ${hours}:${minutes}`;
}

export const ACTION_FILTERS: { key: string; label: string }[] = [
  { key: 'add_stock', label: 'Stock added' },
  { key: 'return_stock', label: 'Returned' },
  { key: 'transfer', label: 'Transferred' },
  { key: 'check_out', label: 'Checked out' },
  { key: 'updated', label: 'Updated' },
  { key: 'retired', label: 'Retired' },
  { key: 'request_created', label: 'Request created' },
  { key: 'request_submitted', label: 'Request submitted' },
  { key: 'request_approved', label: 'Request approved' },
  { key: 'request_ordered', label: 'Ordered' },
  { key: 'request_received', label: 'Received from request' },
  { key: 'request_cancelled', label: 'Cancelled' },
  { key: 'line_unavailable', label: 'Marked unavailable' },
  { key: 'line_backordered', label: 'Backordered' },
  { key: 'line_disputed', label: 'Disputed' },
  { key: 'substitute', label: 'Substitute' },
];

const REQUEST_EVENT_TO_KEY: Record<string, string> = {
  created: 'request_created',
  submitted: 'request_submitted',
  approved: 'request_approved',
  line_approved: 'request_approved',
  line_review_draft: 'request_approved',
  sent_for_requester_review: 'request_approved',
  ordered: 'request_ordered',
  received: 'request_received',
  cancelled: 'request_cancelled',
  line_cancelled: 'request_cancelled',
  line_unavailable: 'line_unavailable',
  line_backordered: 'line_backordered',
  line_disputed: 'line_disputed',
  dispute_resolved: 'line_disputed',
  substitute_counter_proposed: 'substitute',
  line_split: 'updated',
  links_updated: 'updated',
};

export function movementActionKey(eventType: string, movementType: string | null): string {
  const t = movementType || eventType;
  if (t === 'add_stock' || eventType === 'Added') return 'add_stock';
  if (t === 'return_stock') return 'return_stock';
  if (t === 'transfer') return 'transfer';
  if (t === 'check_out') return 'check_out';
  if (t === 'request_fulfilled') return 'request_received';
  if (eventType === 'Retired' || t === 'Retired') return 'retired';
  if (eventType === 'Edited') return 'updated';
  return t;
}

export function requestActionKey(eventType: string): string {
  return REQUEST_EVENT_TO_KEY[eventType] ?? eventType;
}

export function actionLabelForKey(key: string): string {
  return ACTION_FILTERS.find(a => a.key === key)?.label
    ?? key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

export function activityBadgeStyle(actionKey: string, isDark: boolean): { backgroundColor: string; color: string } {
  switch (actionKey) {
    case 'check_out':
      return { backgroundColor: isDark ? 'rgba(217,119,6,0.2)' : '#fef3c7', color: isDark ? '#fbbf24' : '#b45309' };
    case 'transfer':
      return { backgroundColor: isDark ? 'rgba(59,130,246,0.2)' : '#dbeafe', color: isDark ? '#60a5fa' : '#1d4ed8' };
    case 'return_stock':
      return { backgroundColor: isDark ? 'rgba(20,184,166,0.2)' : '#ccfbf1', color: isDark ? '#2dd4bf' : '#0f766e' };
    case 'updated':
      return { backgroundColor: isDark ? 'rgba(107,114,128,0.25)' : '#f3f4f6', color: isDark ? '#9ca3af' : '#4b5563' };
    case 'retired':
    case 'request_cancelled':
    case 'line_unavailable':
      return { backgroundColor: isDark ? 'rgba(239,68,68,0.2)' : '#fee2e2', color: isDark ? '#f87171' : '#b91c1c' };
    case 'request_submitted':
    case 'line_backordered':
      return { backgroundColor: isDark ? 'rgba(234,179,8,0.2)' : '#fef9c3', color: isDark ? '#facc15' : '#a16207' };
    case 'request_approved':
      return { backgroundColor: isDark ? 'rgba(14,165,233,0.2)' : '#e0f2fe', color: isDark ? '#7dd3fc' : '#0369a1' };
    case 'request_ordered':
    case 'substitute':
      return { backgroundColor: isDark ? 'rgba(99,102,241,0.2)' : '#e0e7ff', color: isDark ? '#a5b4fc' : '#4338ca' };
    case 'request_created':
      return { backgroundColor: isDark ? 'rgba(107,114,128,0.25)' : '#f3f4f6', color: isDark ? '#d1d5db' : '#4b5563' };
    case 'line_disputed':
      return { backgroundColor: isDark ? 'rgba(244,63,94,0.2)' : '#ffe4e6', color: isDark ? '#fb7185' : '#be123c' };
    case 'add_stock':
    case 'request_received':
    default:
      return { backgroundColor: isDark ? 'rgba(22,163,74,0.2)' : '#dcfce7', color: isDark ? '#4ade80' : '#15803d' };
  }
}
